/**
 * Minimal observable state container.
 *
 * Subscribers are called synchronously on every dispatch. Intentionally no
 * memoisation layer — scenes diff via shallow equality on the slices they
 * care about.
 *
 * @packageDocumentation
 */

import { INITIAL_STATE } from './types.js';
import { reduce, type Action } from './reducer.js';
import type { GameState } from './types.js';

type Listener = (state: GameState, prev: GameState) => void;

/**
 * Tiny observable store. One instance per page; holds {@link GameState},
 * dispatches {@link Action}s, notifies subscribers synchronously.
 */
export class Store {
  private state: GameState = INITIAL_STATE;
  private listeners = new Set<Listener>();

  /** Read the current state. Callers should not mutate. */
  getState(): GameState {
    return this.state;
  }

  /**
   * Apply an action. If the reducer returns the same reference (no-op),
   * subscribers are not notified. Returns the new state for chaining.
   */
  dispatch(action: Action): GameState {
    const prev = this.state;
    const next = reduce(prev, action);
    if (next === prev) return prev;
    this.state = next;
    for (const l of this.listeners) l(next, prev);
    return next;
  }

  /** Register a listener. Returns an unsubscribe function. */
  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  }
}
